import {
  useMutation,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from "@tanstack/react-query";
import { axios } from "@via/api/axios.tsx";
import { useNotifications } from "@via/hooks/useNotifications.tsx";
import { ErrorNotification } from "@via/utils/interfaces.tsx";
import {
  DeleteSystemBannerResponse,
  GetSystemBannerHistoryResponse,
  GetSystemBannerResponse,
  SaveSystemBannerRequestBody,
  SaveSystemBannerResponse,
} from "common";

const SYSTEM_BANNER_QUERY_KEY = "/api/admin/system-banner";
const SYSTEM_BANNER_HISTORY_QUERY_KEY = "/api/admin/system-banner/history";

// Fetch current system banner configuration
const fetchSystemBanner = async (): Promise<GetSystemBannerResponse> => {
  try {
    const res = await axios.get(SYSTEM_BANNER_QUERY_KEY);
    return res.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const useGetSystemBanner = (): UseQueryResult<
  GetSystemBannerResponse,
  Error
> => {
  return useQuery<GetSystemBannerResponse, Error>({
    queryKey: [SYSTEM_BANNER_QUERY_KEY],
    queryFn: fetchSystemBanner,
    retry: false,
  });
};

// Save (create or update) system banner
export const useSaveSystemBanner = (): UseMutationResult<
  SaveSystemBannerResponse,
  ErrorNotification,
  SaveSystemBannerRequestBody
> => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();

  return useMutation<
    SaveSystemBannerResponse,
    ErrorNotification,
    SaveSystemBannerRequestBody
  >({
    mutationFn: async (payload: SaveSystemBannerRequestBody) => {
      const res = await axios.post(SYSTEM_BANNER_QUERY_KEY, payload);
      return res.data;
    },
    onSuccess: () => {
      setNotification({
        id: "save-system-banner-success",
        message: "Notification Banner Saved",
        details: "The notification banner has been updated successfully.",
        variant: NOTIFICATION.SUCCESS,
      });
      queryClient.invalidateQueries({
        queryKey: [SYSTEM_BANNER_QUERY_KEY],
        refetchType: "all",
      });
      queryClient.invalidateQueries({
        queryKey: [SYSTEM_BANNER_HISTORY_QUERY_KEY],
        refetchType: "all",
      });
      // Also invalidate the user query to refresh the banner in the session
      queryClient.invalidateQueries({
        queryKey: ["/api/auth/v1/user"],
        refetchType: "all",
      });
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "save-system-banner-error",
        message: "Failed to Save Banner",
        details:
          error?.response?.data?.message ??
          "Failed to save notification banner",
        variant: NOTIFICATION.ERROR,
      });
    },
  });
};

// Fetch banner history
const fetchBannerHistory = async (
  take: number,
  skip: number
): Promise<GetSystemBannerHistoryResponse> => {
  try {
    const res = await axios.get(
      `${SYSTEM_BANNER_HISTORY_QUERY_KEY}?take=${take}&skip=${skip}`
    );
    return res.data;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const useGetBannerHistory = (
  page: number = 0,
  pageSize: number = 10
): UseQueryResult<GetSystemBannerHistoryResponse, Error> => {
  const skip = page * pageSize;
  const take = pageSize;

  return useQuery<GetSystemBannerHistoryResponse, Error>({
    queryKey: [SYSTEM_BANNER_HISTORY_QUERY_KEY, take, skip],
    queryFn: () => fetchBannerHistory(take, skip),
    retry: false,
  });
};

// Delete banner from history
export const useDeleteBanner = (): UseMutationResult<
  DeleteSystemBannerResponse,
  ErrorNotification,
  { id: number }
> => {
  const queryClient = useQueryClient();
  const { setNotification, NOTIFICATION } = useNotifications();

  return useMutation<
    DeleteSystemBannerResponse,
    ErrorNotification,
    { id: number }
  >({
    mutationFn: async ({ id }: { id: number }) => {
      const res = await axios.delete(`${SYSTEM_BANNER_QUERY_KEY}/${id}`);
      return res.data;
    },
    onSuccess: () => {
      setNotification({
        id: "delete-banner-success",
        message: "Banner Deleted",
        details: "The notification banner has been removed from history.",
        variant: NOTIFICATION.SUCCESS,
      });
      queryClient.invalidateQueries({
        queryKey: [SYSTEM_BANNER_QUERY_KEY],
        refetchType: "all",
      });
      queryClient.invalidateQueries({
        queryKey: [SYSTEM_BANNER_HISTORY_QUERY_KEY],
        refetchType: "all",
      });
      // Also invalidate the user query in case the deleted banner was active
      queryClient.invalidateQueries({
        queryKey: ["/api/auth/v1/user"],
        refetchType: "all",
      });
    },
    onError: (error: ErrorNotification) => {
      setNotification({
        id: "delete-banner-error",
        message: "Failed to Delete Banner",
        details:
          error?.response?.data?.message ??
          "Failed to delete notification banner",
        variant: NOTIFICATION.ERROR,
      });
    },
  });
};
